import { derivedId, newId, type AssetImportStageV1, type AssetKind } from '@gk/contracts'
import { cleanDate, cleanHours, cleanYear, type Mapping } from './mapping.ts'

export const CHUNK = 200

export interface StageInput {
  orgId: string
  fileName: string
  sha256: string
  source: 'csv' | 'xlsx'
  headers: string[]
  rows: string[][]
  mapping: Mapping
  kindMap: Record<string, AssetKind>
  defaults: { kind?: AssetKind; asset_type_code?: string }
  /** optional client-proposed tags, one per row (when the file has no code column) */
  generatedTags?: string[]
}

/** The batch id the server will derive: v5(org, file hash). Same file -> same batch, which is how a second import is recognised. */
export const batchIdFor = (orgId: string, sha256: string): string => derivedId(orgId, sha256)

/** Only the MAPPED columns are sent (privacy and payload size), cleaned where the cleaning is unambiguous; the server normalises again. */
export function buildStagePayloads(i: StageInput): AssetImportStageV1[] {
  const used = [...new Set(Object.values(i.mapping).filter((h): h is string => !!h))]
  const idx = new Map(i.headers.map((h, n) => [h, n]))
  const cleaners: Record<string, (v: string) => string> = {}
  if (i.mapping.current_hours) cleaners[i.mapping.current_hours] = cleanHours
  if (i.mapping.acquired_on) cleaners[i.mapping.acquired_on] = cleanDate
  if (i.mapping.year) cleaners[i.mapping.year] = cleanYear
  const rows = i.rows.map((r, n) => {
    const cells: Record<string, string> = {}
    for (const h of used) { const v = (r[idx.get(h)!] ?? '').slice(0, 500); cells[h] = (cleaners[h] ?? ((x: string) => x))(v) }
    const tag = i.generatedTags?.[n]
    return { row_no: n + 1, ...(tag ? { tag } : {}), cells }
  })
  const chunkCount = Math.max(1, Math.ceil(rows.length / CHUNK))
  const base = {
    batch_id: batchIdFor(i.orgId, i.sha256), file_sha256: i.sha256, file_name: i.fileName.slice(0, 200), source: i.source,
    mapping: Object.fromEntries(Object.entries(i.mapping).filter(([, h]) => !!h)) as AssetImportStageV1['mapping'],
    ...(Object.keys(i.defaults).length ? { defaults: i.defaults } : {}),
    ...(Object.keys(i.kindMap).length ? { kind_map: i.kindMap } : {}),
    total_rows: rows.length, chunk_count: chunkCount,
  }
  return Array.from({ length: chunkCount }, (_, c) => ({ ...base, chunk_no: c + 1, rows: rows.slice(c * CHUNK, (c + 1) * CHUNK) }))
}

export const newImportCommandId = newId
